import { useEffect, useState } from 'react';
import { useMCPStore } from '../../store/mcp';
import { useCreditsStore } from '../../store/credits';
import { useSettingsStore } from '../../store/settings';
import { createApiClient, type N8nInstance } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../../lib/api-config';
import { getMaxInstances, TIER_INFO, type SubscriptionTier } from '@n8n-builder/shared';

const INSTANCE_COLORS = [
  '#7C9CF5', '#7ED4A6', '#F5A873', '#C78CF5',
  '#F57C9C', '#5CC8D4', '#F5D76E', '#A0A0A0',
  '#F5826E', '#6EC5F5', '#B8E06E', '#E08CF5',
  '#F5B8D4', '#8CE0D4', '#D4B896', '#9CA8F5',
];

function ServerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors = {
    connected: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
    unknown: 'bg-gray-100 text-gray-600',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors[status as keyof typeof colors] || colors.unknown}`}>
      {status === 'connected' && <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-500" />}
      {status === 'error' && <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-red-500" />}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export function N8nConnectionTab() {
  const {
    instances,
    isLoading,
    addInstance,
    updateInstance,
    removeInstance,
    setInstanceActive,
    fetchInstances,
  } = useMCPStore();

  // Modal states
  const [showAddInstanceModal, setShowAddInstanceModal] = useState(false);
  const [showEditInstanceModal, setShowEditInstanceModal] = useState(false);
  const [editingInstance, setEditingInstance] = useState<N8nInstance | null>(null);

  // Form states
  const [instanceName, setInstanceName] = useState('');
  const [instanceUrl, setInstanceUrl] = useState('');
  const [instanceApiKey, setInstanceApiKey] = useState('');
  const [editColor, setEditColor] = useState<string | null>(null);

  // Action states
  const [isSavingInstance, setIsSavingInstance] = useState(false);
  const [deletingInstanceId, setDeletingInstanceId] = useState<string | null>(null);
  const [testingInstanceId, setTestingInstanceId] = useState<string | null>(null);

  const subscription = useCreditsStore((s) => s.subscription) as SubscriptionTier;
  const openSettingsModal = useSettingsStore((s) => s.openSettingsModal);
  const maxInstances = getMaxInstances(subscription);
  const canAddMore = instances.length < maxInstances;

  const getApiClient = () => createApiClient(API_BASE_URL);

  // Load instances on mount (cache-aware)
  useEffect(() => {
    fetchInstances();
  }, []);

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  const resetInstanceForm = () => {
    setInstanceName('');
    setInstanceUrl('');
    setInstanceApiKey('');
    setEditColor(null);
    setEditingInstance(null);
  };

  const [instanceError, setInstanceError] = useState<string | null>(null);

  const handleAddInstance = async () => {
    if (!instanceName.trim() || !instanceUrl.trim() || !instanceApiKey.trim()) return;

    setIsSavingInstance(true);
    setInstanceError(null);
    try {
      const apiClient = getApiClient();
      const result = await apiClient.createInstance(
        instanceName.trim(),
        instanceUrl,
        instanceApiKey
      );

      if (result.error) {
        // Instance limit reached — close modal and show upgrade
        if (result.error.includes('Upgrade to Business') || result.error.includes('INSTANCE_LIMIT_REACHED')) {
          setShowAddInstanceModal(false);
          resetInstanceForm();
          openSettingsModal('subscription');
          return;
        }
        setInstanceError(result.error);
        return;
      }

      if (result.data?.instance) {
        const newInstance = result.data.instance;
        addInstance(newInstance);
        setShowAddInstanceModal(false);
        resetInstanceForm();

        // Auto-test the new instance
        setTestingInstanceId(newInstance.id);
        try {
          const testResult = await apiClient.testInstance(newInstance.id);
          if (testResult.data?.connected) {
            updateInstance(newInstance.id, { status: 'connected' });
            // Auto-activate on successful connection
            const activateResult = await apiClient.activateInstance(newInstance.id);
            if (activateResult.data?.success) {
              setInstanceActive(newInstance.id);
            }
          } else {
            updateInstance(newInstance.id, { status: 'error' });
          }
        } catch {
          updateInstance(newInstance.id, { status: 'error' });
        } finally {
          setTestingInstanceId(null);
        }
      }
    } catch (error) {
      console.error('Failed to add instance:', error);
      setInstanceError('Failed to add instance. Please try again.');
    } finally {
      setIsSavingInstance(false);
    }
  };

  const handleEditInstance = (instance: N8nInstance) => {
    setEditingInstance(instance);
    setInstanceName(instance.name);
    setInstanceUrl(instance.url);
    setInstanceApiKey('');
    setEditColor(instance.color ?? null);
    setShowEditInstanceModal(true);
  };

  const handleUpdateInstance = async () => {
    if (!editingInstance) return;

    setIsSavingInstance(true);
    try {
      const apiClient = getApiClient();
      const data: Partial<{ name: string; url: string; apiKey: string; color: string }> = {};

      if (instanceName !== editingInstance.name) {
        data.name = instanceName;
      }
      if (instanceUrl !== editingInstance.url) {
        data.url = instanceUrl;
      }
      if (instanceApiKey) {
        data.apiKey = instanceApiKey;
      }
      if (editColor !== (editingInstance.color ?? null)) {
        data.color = editColor ?? '';
      }

      const result = await apiClient.updateInstance(editingInstance.id, data);

      if (result.data?.instance) {
        updateInstance(editingInstance.id, result.data.instance);
        setShowEditInstanceModal(false);
        resetInstanceForm();
      }
    } catch (error) {
      console.error('Failed to update instance:', error);
    } finally {
      setIsSavingInstance(false);
    }
  };

  const handleDeleteInstance = async (id: string) => {
    if (!confirm('Delete this n8n instance? This action cannot be undone.')) return;

    setDeletingInstanceId(id);
    try {
      const apiClient = getApiClient();
      const result = await apiClient.deleteInstance(id);

      if (result.data?.success) {
        removeInstance(id);
      }
    } catch (error) {
      console.error('Failed to delete instance:', error);
    } finally {
      setDeletingInstanceId(null);
    }
  };

  const handleTestInstance = async (id: string) => {
    setTestingInstanceId(id);
    try {
      const apiClient = getApiClient();
      const result = await apiClient.testInstance(id);

      if (result.data) {
        updateInstance(id, {
          status: result.data.connected ? 'connected' : 'error',
        });
      }
    } catch (error) {
      console.error('Failed to test instance:', error);
      updateInstance(id, { status: 'error' });
    } finally {
      setTestingInstanceId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">n8n Instances</h2>
          <p className="text-sm text-gray-500">Manage your n8n instances for workflow deployment.</p>
        </div>
        {canAddMore ? (
          <button
            onClick={() => setShowAddInstanceModal(true)}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add Instance
          </button>
        ) : (
          <button
            onClick={() => openSettingsModal('subscription')}
            className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-purple-700"
          >
            Upgrade to Business
          </button>
        )}
      </div>

      {/* Instance limit banner */}
      {!canAddMore && instances.length > 0 && (
        <div className="rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-800">
          {instances.length > maxInstances ? (
            <>You have {instances.length} instances but your {TIER_INFO[subscription].name} plan allows {maxInstances}. Existing instances will continue to work.</>
          ) : (
            <>Your {TIER_INFO[subscription].name} plan allows {maxInstances} n8n instance{maxInstances === 1 ? '' : 's'}. <button onClick={() => openSettingsModal('subscription')} className="font-medium underline hover:text-purple-900">Upgrade to Business</button> for unlimited instances.</>
          )}
        </div>
      )}

      {/* Instance Table */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 rounded-lg skeleton" />
          ))}
        </div>
      ) : instances.length === 0 ? (
        <div className="rounded-xl border border-gray-200 p-8 text-center">
          <ServerIcon className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-sm text-gray-500">No n8n instances yet</p>
          <p className="mt-1 text-xs text-gray-400">Add an n8n instance to deploy workflows</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">URL</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {instances.map((instance) => (
                <tr key={instance.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="flex items-center gap-2">
                      {instance.color && (
                        <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: instance.color }} />
                      )}
                      <span className="text-sm font-medium text-gray-900">{instance.name}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <code className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">{instance.url}</code>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <StatusBadge status={instance.status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatDate(instance.createdAt)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <button
                      onClick={() => handleTestInstance(instance.id)}
                      disabled={testingInstanceId === instance.id}
                      className="mr-2 rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                      {testingInstanceId === instance.id ? 'Testing...' : 'Test'}
                    </button>
                    <button
                      onClick={() => handleEditInstance(instance)}
                      className="mr-2 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      title="Edit instance"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteInstance(instance.id)}
                      disabled={deletingInstanceId === instance.id}
                      className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      title="Delete instance"
                    >
                      {deletingInstanceId === instance.id ? (
                        <SpinnerIcon className="h-4 w-4" />
                      ) : (
                        <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Help text */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
        <h3 className="text-sm font-semibold text-gray-900">Need help connecting?</h3>
        <p className="mt-1 text-sm text-gray-600">
          To get your n8n API key, go to your n8n instance, navigate to{' '}
          <span className="font-medium text-gray-800">Settings &gt; API</span>, and create a new API key.
        </p>
        <p className="mt-2 text-sm text-gray-600">
          Make sure your n8n instance is accessible from the internet if you're using this app remotely.
        </p>
      </div>

      {/* Add Instance Modal */}
      {showAddInstanceModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {canAddMore ? 'Add n8n Instance' : 'Instance Limit Reached'}
              </h2>
              <button
                onClick={() => {
                  setShowAddInstanceModal(false);
                  resetInstanceForm();
                  setInstanceError(null);
                }}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {canAddMore ? (
              <>
                <div className="space-y-4 p-6">
                  {instanceError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {instanceError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Instance Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={instanceName}
                      onChange={(e) => setInstanceName(e.target.value.slice(0, 24))}
                      maxLength={24}
                      placeholder="e.g., Production"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-400 text-right">{instanceName.length}/24</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      n8n URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      value={instanceUrl}
                      onChange={(e) => setInstanceUrl(e.target.value)}
                      placeholder="https://your-n8n.example.com"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      n8n API Key <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={instanceApiKey}
                      onChange={(e) => setInstanceApiKey(e.target.value)}
                      placeholder="n8n_api_..."
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Generate an API key in your n8n instance: Settings {">"} API
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
                  <button
                    onClick={() => {
                      setShowAddInstanceModal(false);
                      resetInstanceForm();
                      setInstanceError(null);
                    }}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddInstance}
                    disabled={isSavingInstance || !instanceName.trim() || !instanceUrl.trim() || !instanceApiKey.trim()}
                    className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {isSavingInstance ? (
                      <>
                        <SpinnerIcon className="h-4 w-4" />
                        Saving...
                      </>
                    ) : (
                      'Add Instance'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-6 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                    <ServerIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <p className="text-sm text-gray-600">
                    Your <span className="font-medium">{TIER_INFO[subscription].name}</span> plan allows {maxInstances} n8n instance{maxInstances === 1 ? '' : 's'}.
                  </p>
                  <p className="mt-2 text-sm text-gray-600">
                    Upgrade to <span className="font-medium">Business</span> to connect unlimited n8n instances.
                  </p>
                </div>
                <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
                  <button
                    onClick={() => {
                      setShowAddInstanceModal(false);
                      resetInstanceForm();
                    }}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowAddInstanceModal(false);
                      resetInstanceForm();
                      openSettingsModal('subscription');
                    }}
                    className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
                  >
                    Upgrade to Business
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Instance Modal */}
      {showEditInstanceModal && editingInstance && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Edit n8n Instance</h2>
              <button
                onClick={() => {
                  setShowEditInstanceModal(false);
                  resetInstanceForm();
                }}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instance Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value.slice(0, 24))}
                  maxLength={24}
                  placeholder="e.g., Production"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-400 text-right">{instanceName.length}/24</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  n8n URL
                </label>
                <input
                  type="url"
                  value={instanceUrl}
                  onChange={(e) => setInstanceUrl(e.target.value)}
                  placeholder="https://your-n8n.example.com"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  n8n API Key <span className="text-xs text-gray-400">(leave blank to keep current)</span>
                </label>
                <input
                  type="password"
                  value={instanceApiKey}
                  onChange={(e) => setInstanceApiKey(e.target.value)}
                  placeholder="Enter new API key to change"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <div className="flex flex-wrap gap-2">
                  {INSTANCE_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEditColor(c)}
                      className="relative h-7 w-7 rounded-full transition-transform hover:scale-110 focus:outline-none"
                      style={{ backgroundColor: c }}
                      title={c}
                    >
                      {editColor === c && (
                        <CheckIcon className="absolute inset-0 m-auto h-3.5 w-3.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => {
                  setShowEditInstanceModal(false);
                  resetInstanceForm();
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateInstance}
                disabled={isSavingInstance || !instanceName.trim()}
                className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {isSavingInstance ? (
                  <>
                    <SpinnerIcon className="h-4 w-4" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
